import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useSearchParams } from 'react-router-dom';
import Drawer from '@app/components/Drawer';
import { titleCase } from './api';
import './workspace.css';
export const bookTitle = (book?: { authoring?: { title?: string }; runbook_name?: string }) => book?.authoring?.title || book?.runbook_name || 'Runbook';
export const Badge = ({ value }: { value: string }) => <span className={`rb-badge rb-badge--${value}`}>{titleCase(value)}</span>;
export const Json = ({ value }: { value: unknown }) => <pre className="rw-json">{JSON.stringify(value, null, 2)}</pre>;
export function RunbookNav() {
  const [params] = useSearchParams();
  const org = params.get('orgId');
  return <nav className="rb-tabs" aria-label="Runbook views">{['library', 'schedules', 'executions'].map(view => <NavLink key={view} to={`/app/runbooks/${view}${org ? `?orgId=${encodeURIComponent(org)}` : ''}`}>{titleCase(view)}</NavLink>)}</nav>;
}
export const DemoNotice = () => import.meta.env.DEV && import.meta.env.VITE_RUNBOOK_SCHEDULER_MOCK === 'true'
  ? <div className="rw-demo">Preview environment · Sample executions and outputs. Changes stay in this browser session.</div> : null;
export function Panel({ title, subtitle, children, footer, onClose }: { title: string; subtitle?: string; children: ReactNode; footer?: ReactNode; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const appRoot = document.getElementById('root');
    const wasInert = appRoot?.inert ?? false;
    if (appRoot) appRoot.inert = true;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const list = () => Array.from(ref.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), a[href], summary, [tabindex="0"]') ?? []).filter(el => el.getClientRects().length);
    list()[0]?.focus();
    const trap = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const items = list(), first = items[0], last = items.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    const element = ref.current;
    element?.addEventListener('keydown', trap);
    return () => { element?.removeEventListener('keydown', trap); if (appRoot) appRoot.inert = wasInert; document.body.style.overflow = overflow; previous?.focus(); };
  }, []);
  return createPortal(<div className="rb-panel" ref={ref}><Drawer open title={title} subtitle={subtitle} onClose={onClose} footer={footer}>{children}</Drawer></div>, document.body);
}
export function downloadText(text: string, filename: string, mime = 'text/plain') {
  const url = URL.createObjectURL(new Blob([text], { type: mime }));
  const a = document.createElement('a'); a.href = url; a.download = filename.replace(/[\\/]/g, '-'); a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
